import type { NavLink } from 'components/molecules/NavLinks/mobiNavLinks';

export interface ActiveNav {
  activeNavId: string | null;
  activeSubId: string | null;
}

// decode, strip query string and hash, remove trailing slash (except root)
export function normalizePath(path: string): string {
  if (!path) return '/';
  let p = path;
  try {
    p = decodeURIComponent(p);
  } catch (e) {
    // ignore malformed
  }
  // strip
  p = p.split('?')[0].split('#')[0];
  // remove trailing
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return p || '/';
}

/**
 * For nav items without hasFlatSubPaths, sub-menu matching is gated behind
 * the parent href prefix (safe default — avoids false positives).
 *
 * For nav items with hasFlatSubPaths, all sub hrefs are checked regardless
 * of whether they share the parent's path prefix (opt-in cross-path matching).
 */
export function resolveActiveNav(navLinks: NavLink[], asPath: string): ActiveNav {
  const pathOnly = normalizePath(asPath);

  for (const nav of navLinks) {
    if (nav.subMenu?.length) {
      const navHref = normalizePath(nav.href);
      const shouldCheckSubs =
        nav.hasFlatSubPaths || pathOnly === navHref || pathOnly.startsWith(navHref + '/');

      if (shouldCheckSubs) {
        // check longest normalized href for most specific
        const sortedSubs = [...nav.subMenu].sort(
          (a, b) => normalizePath(b.href).length - normalizePath(a.href).length,
        );
        const matchedSub = sortedSubs.find((sub) => {
          const subHref = normalizePath(sub.href);
          return pathOnly === subHref || pathOnly.startsWith(subHref + '/');
        });
        if (matchedSub) {
          return { activeNavId: nav.id, activeSubId: matchedSub.id };
        }
      }
    }

    const navHref = normalizePath(nav.href);
    if (pathOnly === navHref || pathOnly.startsWith(navHref + '/')) {
      return { activeNavId: nav.id, activeSubId: null };
    }
  }

  return { activeNavId: null, activeSubId: null };
}
